import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import api from "../../../services/api";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import toastOptions from "../../../utils/toastOptions";
import {
  displayDateTime,
  handleFocus,
  phoneMask,
} from "../../../utils/masks";
import { Gender } from "../../../models/enums/Gender";
import { useNavigate, useParams } from "react-router-dom";
import { getValidator } from "../../../utils/validatorsYup";

import { authenticationService } from "../../../services/auth.service";

import "../../shared/modal.css";
import { StatusType } from "../../../models/enums/StatusType";
import IPersonDTO from "../../../models/IPersonDTO";
import Param from "../../../models/Param";

import IPersonCampaignDTO from "../../../models/IPersonCampaignDTO";
import CampaignPaymentList from "../campaign-payment-list/CampaignPaymentList";

type RouteParams = {
  id: string;
}

export default function CampaignPersonAddEdit() {
  const params = useParams<RouteParams>();
  const [person, setPerson] = useState<IPersonCampaignDTO>();
  const [isDisabled, setDisabled] = useState<boolean>(true);
  const [canChange, setCanChange] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [isAdding, setAdding] = useState<boolean>(false);
  const [congregations, setCongregations] = useState<Param[]>([]);
  const [tabIndex, setTabIndex] = useState<number>(0);

  const navigate = useNavigate();

  const schema = yup.object().shape({
    name: yup
      .string()
      .required("Nome obrigatório")
      .max(100, (val) => `Máximo ${val.max} caracteres`)
      .min(3, (val) => `Mínimo ${val.min} caracteres`),
    gender: yup.string().required("Gênero obrigatório"),
    phone: yup.string().required(`Telefone é obrigatório`),
    phoneConfirmation: yup
      .string()
      .when([], {
        is: () => isAdding, // só obrigatório se estiver adicionando
        then: yup
          .string()
          .required("Confirmação do telefone é obrigatória")
          .oneOf([yup.ref("phone"), ''], "Os números de telefone não coincidem"),
        otherwise: yup.string().nullable(),
    }),
    email: yup.string().required(`E-mail é obrigatório`).max(100, (val) => `Máximo ${val.max} caracteres`),
    note: yup.string()
      .max(250, (val) => `Máximo ${val.max} caracteres`),
    idCongregation: getValidator({isNumeric: true}).required('Congregação obrigatória'),
    dueDay: getValidator({isNumeric: true}).required('Dia da doação obrigatório'),
    dueAmount: getValidator({isNumeric: true}).required('Valor da doação obrigatório')
  });

  const {
    register,
    handleSubmit,
    reset,
    errors,
    formState
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    
    init();
  }, [params.id]);

  const init = async () => {
    const dataCongregations = await api.get('congregations/dashboard');
    setCongregations(dataCongregations.data);

    const id = +(params.id ?? 0);

    const adding = id ? id <= 0 : true;

    setAdding(adding);

    if (!adding) {
      handleEdit(id);
    }

    setDisabled((authenticationService.isAuxCampaign() && !adding) || authenticationService.isReadOnly());

    setCanChange(authenticationService.canChangeCampaign());
  };

  const handleEdit = async (id: number) => {
    try {
      const { data } = await api.get(`/campaigns/${id}`);

      setPerson(data.item);

    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.warning(m, toastOptions);
      });
    }
  };

  const onSubmit = async (data: IPersonDTO) => {
    try {
      data.id = person?.id ?? 0;

      if (!data?.id) {
        const result = await api.post('campaigns', data);
        toast.success('Pessoa cadastrada com sucesso!', toastOptions);

        navigate(-1);
      } else {
        const { data: result } = await api.put('campaigns', data);
        toast.success('Pessoa atualizada com sucesso!', toastOptions);

        const m = Object.assign(data, {});
        result.item.item.id = 0;
        result.item.item.id = m.id;
        setPerson(result.item.item);
      }
      
      setRefresh(!refresh);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    }
  };

  return params.id && !person ? (
    <div className="uk-flex uk-flex-center">
      <div uk-spinner="true; ratio:2">Carregando...</div>
    </div>
  ) : (
    <div id="page-edit-person-campaign" className="uk-section uk-section-default uk-padding-small">
      {
        (!isAdding && !isDisabled && authenticationService.canChangeCampaign()) && (
          <ul uk-tab="true">
            <li className={tabIndex == 0 ? 'uk-active' : ''} onClick={()=> setTabIndex(0)}>
              <a href="#" onClick={(ev) => { ev.preventDefault(); }}>Dados</a>
            </li>
            <li className={tabIndex == 1 ? 'uk-active' : ''} onClick={()=> setTabIndex(1)}>
              <a href="#" onClick={(ev) => { ev.preventDefault(); }}>Pagamentos</a>
            </li>
          </ul>
        )
      }
      <form onReset={reset} 
        hidden={tabIndex != 0}
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
          return handleSubmit<IPersonDTO>(onSubmit)(e)
        }}
      >
        <div className="uk-modal-body">
          <div className="uk-grid-small" uk-grid="true">
            <div className="uk-width-3-4@s">
              <label className="uk-form-label" htmlFor="name">
                Nome
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Nome"
                className="uk-input uk-form-small"
                defaultValue={person?.name}
                style={{ textTransform: "uppercase" }}
                autoComplete={"off"}
                onFocus={handleFocus}
                ref={register}
                disabled={isDisabled}
              />
              <small className="uk-text-danger">{errors.name?.message}</small>
            </div>
            <div className="uk-width-1-4@s">
              <label className="uk-form-label" htmlFor="gender">
                Sexo
              </label>
              <div
                uk-form-custom="target: > * > span:first-child"
                className="uk-width-1"
              >
                <select
                  className="uk-form-small"
                  id="gender"
                  name="gender"
                  defaultValue={person?.gender}
                  ref={register}
                  disabled={isDisabled}
                >
                  <option value="" key={`gender0`}>
                    Selecione...
                  </option>
                  <option value={Gender.FEMALE} key={`gender1`}>
                    Feminino
                  </option>
                  <option value={Gender.MALE} key={`gender2`}>
                    Masculino
                  </option>
                </select>
                <button
                  className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                  style={{ textTransform: "none" }}
                  type="button"
                  tabIndex={-1}
                  disabled={isDisabled}
                >
                  <span></span>
                  <span uk-icon="icon: chevron-down"></span>
                </button>
              </div>
              <small className="uk-text-danger">{errors.gender?.message}</small>
            </div>
            <div className="uk-width-1-2@s">
              <label className="uk-form-label" htmlFor="email">
                E-mail
              </label>
              <input
                type="text"
                id="email"
                name="email"
                placeholder="email@email.com"
                className="uk-input uk-form-small"
                defaultValue={person?.email}
                autoComplete={"off"}
                ref={register}
                disabled={isDisabled}
              />
              <small className="uk-text-danger">{errors.email?.message}</small>
            </div>
            <div className="uk-width-1-4@s">
              <label className="uk-form-label" htmlFor="phone">
                Celular
              </label>
              <input
                type="text"
                id="phone"
                name="phone"
                placeholder="(99) 99999-9999"
                className="uk-input uk-form-small"
                defaultValue={phoneMask(person?.phone ?? "")}
                ref={register}
                autoComplete={"off"}
                onChange={(e) => {
                  e.target.value = phoneMask(e.target.value);
                }}
                disabled={isDisabled}
              />
              <small className="uk-text-danger">{errors.phone?.message}</small>
            </div>
            {isAdding && (
              <div className="uk-width-1-4@s">
                <label className="uk-form-label" htmlFor="phoneConfirmation">
                  Confirmar Celular
                </label>
                <input
                  type="text"
                  id="phoneConfirmation"
                  name="phoneConfirmation"
                  placeholder="(99) 99999-9999"
                  className="uk-input uk-form-small"
                  autoComplete="off"
                  ref={register}
                  onChange={(e) => {
                    e.target.value = phoneMask(e.target.value);
                  }}
                  onPaste={(e) => e.preventDefault()} // block paste
                  onCopy={(e) => e.preventDefault()} // block copy
                  onCut={(e) => e.preventDefault()} // block cut
                />
                <small className="uk-text-danger">
                  {errors.phoneConfirmation?.message}
                </small>
              </div>
            )}
            <div className="uk-width-1-2@s">
              <label className="uk-form-label" htmlFor="idCongregation">
                Congregação
              </label>
              <div
                uk-form-custom="target: > * > span:first-child"
                className="uk-width-1"
              >
                <select
                  className="uk-form-small"
                  id="idCongregation"
                  name="idCongregation"
                  defaultValue={person?.idCongregation}
                  ref={register}
                  disabled={isDisabled}
                >
                  <option value="" key={`congregation0`}>
                    Selecione...
                  </option>
                  {congregations.map((c) => {
                    return (
                      <option value={c.id} key={`congregation${c.id}`}>
                        {c.description}
                      </option>
                    );
                  })}
                </select>
                <button
                  className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                  style={{ textTransform: "none" }}
                  type="button"
                  tabIndex={-1}
                  disabled={isDisabled}
                >
                  <span></span>
                  <span uk-icon="icon: chevron-down"></span>
                </button>
              </div>
              <small className="uk-text-danger">
                {errors.idCongregation?.message}
              </small>
            </div>
            <div className="uk-width-1-4@s">
              <label className="uk-form-label" htmlFor="dueDay">
                Dia Doação
              </label>
              <div
                uk-form-custom="target: > * > span:first-child"
                className="uk-width-1"
              >
                <select
                  className="uk-form-small"
                  id="dueDay"
                  name="dueDay"
                  defaultValue={person?.dueDay}
                  ref={register}
                  disabled={isDisabled}
                >
                  <option value="" key={`dueDay0`}>Selecione...</option>
                  <option value={5} key={`dueDay1`}>Dia 5</option>
                  <option value={10} key={`dueDay2`}>Dia 10</option>
                  <option value={20} key={`dueDa3`}>Dia 20</option>
                  <option value={30} key={`dueDay4`}>Dia 30</option>
                </select>
                <button
                  className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                  style={{ textTransform: "none" }}
                  type="button"
                  tabIndex={-1}
                  disabled={isDisabled}
                >
                  <span></span>
                  <span uk-icon="icon: chevron-down"></span>
                </button>
              </div>
              <small className="uk-text-danger">{errors.dueDay?.message}</small>
            </div>
            <div className="uk-width-1-4@s">
              <label className="uk-form-label" htmlFor="dueDay">
                Valor Doação
              </label>
              <div
                uk-form-custom="target: > * > span:first-child"
                className="uk-width-1"
              >
                <select
                  className="uk-form-small"
                  id="dueAmount"
                  name="dueAmount"
                  defaultValue={person?.dueAmount}
                  ref={register}
                  disabled={isDisabled}
                >
                  <option value="" key={`dueAmount0`}>
                    Selecione...
                  </option>
                  <option value={50} key={`dueAmount1`}>
                    R$ 50,00
                  </option>
                  <option value={100} key={`dueAmount2`}>
                    R$ 100,00
                  </option>
                  <option value={150} key={`dueAmount3`}>
                    R$ 150,00
                  </option>
                  <option value={200} key={`dueAmount4`}>
                    R$ 200,00
                  </option>
                </select>
                <button
                  className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                  style={{ textTransform: "none" }}
                  type="button"
                  tabIndex={-1}
                  disabled={isDisabled}
                >
                  <span></span>
                  <span uk-icon="icon: chevron-down"></span>
                </button>
              </div>
              <small className="uk-text-danger">
                {errors.dueAmount?.message}
              </small>
            </div>
          </div>
          <div className="uk-grid-small" uk-grid="true">
            <div className="uk-width-1@s">
              <label className="uk-form-label" htmlFor="note">
                Observações
              </label>
              <textarea
                id="note"
                name="note"
                className="uk-textarea uk-form-small"
                rows={4}
                style={{ resize: "none", textTransform: "uppercase" }}
                defaultValue={person?.note}
                autoComplete={"off"}
                ref={register}
                disabled={isDisabled}
              />
              <small className="uk-text-danger">{errors.note?.message}</small>
            </div>
          </div>
          {person?.id && canChange && (
            <div className="uk-grid-small" uk-grid="true">
              <div className="uk-width-1-2@s">
                <span className="uk-text-italic uk-text-small">
                  Adicionado por:
                </span>
                <span className="uk-text-muted uk-text-small uk-margin-small-left">
                  {`${person?.createdBy} - ${displayDateTime(person?.createdDate)}`}
                </span>
              </div>
              {person.modifiedBy && (
                <div className="uk-width-1-2@s">
                  <span className="uk-text-italic uk-text-small">
                    Alterado por:
                  </span>
                  <span className="uk-text-muted uk-text-small uk-margin-small-left">
                    {`${person?.modifiedBy} - ${displayDateTime(person?.modifiedDate)}`}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="uk-modal-footer uk-margin-bottom">
          <div className="uk-grid-small uk-flex uk-flex-center" uk-grid="true">
            <div className="uk-text-center">
              <button
                className="uk-button uk-button-default uk-button-small"
                type="button"
                onClick={() => { authenticationService.isAuxCampaign() ? navigate('/campanhas/pessoas') : navigate(-1)}}
              >
                Voltar
              </button>
            </div>
            {person?.status != StatusType.INACTIVE && (
              <>
                <div className="uk-text-center">
                  <button
                    id="btn-save"
                    disabled={formState.isSubmitting || isDisabled}
                    className="uk-button uk-button-primary uk-button-small uk-margin-left uk-width-1-1"
                    type="submit"
                  >
                    {formState.isSubmitting && <div uk-spinner="true; ratio:0.5"></div>}
                    Salvar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </form>
      {
        <div className="uk-modal-body" hidden={tabIndex != 1}>
          <CampaignPaymentList id={person?.idPerson ?? 0}/>
        </div>
      }
    </div>
  );
}
